<template>
  <section class="admin-content" id="procurement-orders-page">
    <Navbar />
    <main class="admin-main">

      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white flex-wrap" style="padding: 32px 0; gap: 12px;">
            <div class="header-icon-wrap mr-3">
              <i class="fe fe-shopping-cart" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Procurement Orders</h4>
              <small style="opacity: .8;">Marketplace orders and agency drug procurement requests</small>
            </div>
            <router-link v-if="isBuyer" to="/marketplace" class="btn btn-light btn-sm ml-auto">
              <i class="fe fe-shopping-bag mr-1"></i>Shop the marketplace
            </router-link>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div class="row">

            <!-- New Request Form (facilities: drug procurement from agency vendors) -->
            <div class="col-md-5 mb-4" v-if="isBuyer">
              <div class="card shadow-sm form-card">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-plus" style="font-size:13px;"></i></div>
                  <div>
                    <h6 class="mb-0 font-weight-bold text-white">New Procurement Request</h6>
                  </div>
                </div>
                <div class="card-body px-4 py-4">
                  <div class="form-group">
                    <label class="form-label">Vendor</label>
                    <select v-model="form.vendor_id" class="form-control styled-select">
                      <option value="">Select vendor…</option>
                      <option v-for="vendor in vendors" :key="vendor.id" :value="vendor.id">{{ vendor.name }}</option>
                    </select>
                  </div>

                  <div class="form-group">
                    <label class="form-label">Notes</label>
                    <textarea v-model="form.notes" class="form-control styled-select" rows="2"
                      placeholder="Additional notes…"></textarea>
                  </div>

                  <div class="section-divider mb-3">
                    <span class="section-divider-text">Drug Items</span>
                  </div>

                  <div class="item-row" v-for="(row, idx) in form.items" :key="idx">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <span class="small font-weight-bold text-secondary">Item {{ idx + 1 }}</span>
                      <button type="button" class="btn btn-sm btn-outline-danger"
                        style="padding:2px 8px;font-size:.75rem;" @click="removeItem(idx)" v-if="form.items.length > 1">
                        <i class="fe fe-trash-2"></i>
                      </button>
                    </div>
                    <div class="form-group">
                      <label class="form-label-sm">Drug</label>
                      <select v-model="row.drug_id" class="form-control form-control-sm styled-select">
                        <option value="">Select drug…</option>
                        <option v-for="drug in drugs" :key="drug.id" :value="drug.id">{{ drug.drug_name }}</option>
                      </select>
                    </div>
                    <div class="form-group mb-0">
                      <label class="form-label-sm">Quantity</label>
                      <input v-model.number="row.quantity" class="form-control form-control-sm styled-select" min="1"
                        type="number" />
                    </div>
                  </div>

                  <div class="d-flex mt-3" style="gap:8px;">
                    <button type="button" class="btn btn-outline-secondary btn-sm" @click="addItem">
                      <i class="fe fe-plus mr-1"></i>Add Drug
                    </button>
                    <button type="button" class="btn btn-submit ml-auto" @click="createOrder">
                      <i class="fe fe-send mr-1"></i>Submit Request
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Orders Table -->
            <div :class="isBuyer ? 'col-md-7' : 'col-12'" class="mb-4">
              <div class="card shadow-sm table-card">
                <div class="d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
                  <div class="d-flex align-items-center">
                    <i class="fe fe-list text-primary mr-2"></i>
                    <h6 class="mb-0 font-weight-bold">Orders</h6>
                    <span class="badge badge-soft-info ml-2">{{ orders.length }}</span>
                  </div>
                </div>
                <div class="table-responsive">
                  <table class="table align-td-middle mb-0">
                    <thead style="background:#f8fafc;">
                      <tr>
                        <th class="table-th">Order</th>
                        <th class="table-th">Vendor</th>
                        <th class="table-th">Status</th>
                        <th class="table-th">Payment</th>
                        <th class="table-th">Total</th>
                        <th class="table-th"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="order in orders" :key="order.id" class="table-row">
                        <td class="px-4 py-3" style="font-size:.88rem;">
                          <div class="font-weight-bold">{{ order.order_number }}</div>
                          <div class="small text-muted">{{ order.channel === 'marketplace' ? 'Marketplace' : 'Drug procurement' }}</div>
                        </td>
                        <td class="py-3" style="font-size:.85rem;">{{ order.vendor ? order.vendor.name : order.vendor_id
                          }}</td>
                        <td class="py-3">
                          <span :class="'order-badge order-badge--' + (order.status || 'pending')">{{
                            (order.status || '').replace('_', ' ') }}</span>
                        </td>
                        <td class="py-3">
                          <span :class="'pay-badge pay-badge--' + (order.payment_status || 'unpaid')">{{
                            order.payment_status || 'unpaid' }}</span>
                        </td>
                        <td class="py-3" style="font-size:.85rem;">
                          <i class="mdi mdi-currency-ngn"></i>{{ order.total_approved_amount ||
                          order.total_requested_amount }}
                        </td>
                        <td class="py-3 pr-4">
                          <router-link :to="`/procurement/orders/${order.id}`">
                            <button type="button" class="btn btn-sm btn-view"><i
                                class="fe fe-eye mr-1"></i>Open</button>
                          </router-link>
                        </td>
                      </tr>
                      <tr v-if="!orders.length">
                        <td colspan="6" class="text-center py-5 text-muted">
                          <i class="fe fe-inbox" style="font-size:2rem;opacity:.3"></i>
                          <p class="mt-2 mb-0">No orders yet</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";
  let agency = JSON.parse(localStorage.getItem("agency"));

  export default {
    components: { Navbar },
    data() {
      return {
        agency,
        user: null,
        vendors: [],
        drugs: [],
        orders: [],
        form: {
          agency_id: "",
          vendor_id: "",
          notes: "",
          items: [{ drug_id: "", quantity: 1 }],
        },
      };
    },
    computed: {
      isBuyer() {
        return this.user && (this.user.type === "provider" || this.user.type === "provider_employee");
      },
    },
    beforeMount() {
      this.user = JSON.parse(localStorage.getItem("user") || "null");
      this.fetchVendors();
      this.fetchDrugs();
      this.fetchOrders();
    },
    methods: {
      fetchVendors() {
        this.axios.get("/api/v1/auth/vendors", { params: { per_page: 200, agency_id: agency?.id } }).then((res) => {
          this.vendors = res.data.data || [];
        });
      },
      fetchDrugs() {
        this.axios.get(`/api/v1/auth/drug-agency/${agency?.id || ""}`, { params: { per_page: 500 } }).then((res) => {
          this.drugs = (res.data.data || res.data || []).filter(Boolean);
        });
      },
      fetchOrders() {
        // The API limits the list to the caller's own orders (vendor, facility or agency).
        const params = { per_page: 200 };
        this.axios.get("/api/v1/auth/procurement-orders", { params }).then((res) => {
          this.orders = res.data.data || [];
        });
      },
      addItem() {
        this.form.items.push({ drug_id: "", quantity: 1 });
      },
      removeItem(index) {
        if (this.form.items.length === 1) return;
        this.form.items.splice(index, 1);
      },
      createOrder() {
        const payload = {
          agency_id: agency?.id || null,
          vendor_id: this.form.vendor_id,
          notes: this.form.notes,
          items: this.form.items,
        };
        this.axios
          .post("/api/v1/auth/procurement-orders", payload)
          .then(() => {
            this.$breadstick.notify("Procurement request submitted", { position: "top-right" });
            this.form = { agency_id: "", vendor_id: "", notes: "", items: [{ drug_id: "", quantity: 1 }] };
            this.fetchOrders();
          })
          .catch((err) => {
            this.$breadstick.notify(err.response?.data?.message || "Failed to submit request", { position: "top-right" });
          });
      },
    },
  };
</script>

<style scoped>
  .page-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  }

  .header-icon-wrap {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* Form card */
  .form-card {
    border-radius: 14px;
    overflow: hidden;
    border: none;
  }

  .form-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  }

  .section-icon {
    width: 28px;
    height: 28px;
    background: rgba(255, 255, 255, .2);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    flex-shrink: 0;
  }

  .form-label {
    font-weight: 600;
    font-size: .85rem;
    color: #374151;
    margin-bottom: 6px;
  }

  .form-label-sm {
    font-weight: 600;
    font-size: .8rem;
    color: #374151;
    margin-bottom: 4px;
    display: block;
  }

  .styled-select {
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: .88rem;
  }

  .styled-select:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, .1);
    outline: none;
  }

  .item-row {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 10px;
  }

  .section-divider {
    display: flex;
    align-items: center;
  }

  .section-divider::before,
  .section-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }

  .section-divider-text {
    padding: 0 12px;
    font-size: .75rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: .06em;
  }

  /* Table card */
  .table-card {
    border-radius: 12px;
    border: none;
  }

  .table-th {
    font-size: .73rem;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: #64748b;
    font-weight: 600;
    border-bottom: 2px solid #e2e8f0;
    padding: 12px 16px;
  }

  .table-row:hover {
    background: #f8fafc;
  }

  /* Badges */
  .order-badge,
  .pay-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: .75rem;
    font-weight: 600;
    text-transform: capitalize;
  }

  .order-badge--submitted {
    background: #dbeafe;
    color: #1d4ed8;
  }

  .order-badge--approved {
    background: #d1fae5;
    color: #065f46;
  }

  .order-badge--received {
    background: #d1fae5;
    color: #065f46;
  }

  .order-badge--pending {
    background: #fef3c7;
    color: #92400e;
  }

  .order-badge--rejected {
    background: #fee2e2;
    color: #991b1b;
  }

  .order-badge--cancelled {
    background: #e2e8f0;
    color: #475569;
  }

  .order-badge--dispatched {
    background: #ede9fe;
    color: #5b21b6;
  }

  .order-badge--partially_approved {
    background: #d1fae5;
    color: #065f46;
  }

  .pay-badge--paid {
    background: #d1fae5;
    color: #065f46;
  }

  .pay-badge--unpaid {
    background: #fef3c7;
    color: #92400e;
  }

  .btn-submit {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    border: none;
    color: #fff;
    border-radius: 8px;
    font-weight: 600;
    padding: 8px 20px;
  }

  .btn-submit:hover {
    opacity: .9;
    color: #fff;
  }

  .btn-view {
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    color: #1e3a5f;
    background: #fff;
    font-size: .8rem;
  }

  .btn-view:hover {
    background: #f1f5f9;
  }
</style>